import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  Loader2,
  MessageSquare,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  approvePurchaseOrder,
  confirmPurchaseOrder,
  getPurchaseOrder,
  markPoReadyForInspection,
  sendPoMessage,
  startPoProduction,
  updatePurchaseOrder,
} from "../lib/api";
import { WORKSPACE } from "../constants/testIds";

export default function PurchaseOrderDetail() {
  const { poId } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const isAdmin = !!localStorage.getItem("demaxtore_admin_token");
  const [shipmentDate, setShipmentDate] = useState("");
  const [opsNotes, setOpsNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await getPurchaseOrder(poId);
      setPo(data);
      setShipmentDate(data.shipment_date || "");
      setOpsNotes(data.ops_notes || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load PO");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId]);

  const approve = async () => {
    setBusy(true);
    try {
      await approvePurchaseOrder(poId);
      toast.success("Purchase Order approved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed");
    } finally {
      setBusy(false);
    }
  };

  const confirmMfg = async () => {
    setBusy(true);
    try {
      await confirmPurchaseOrder(poId);
      toast.success("Manufacturer confirmed");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Confirmation failed");
    } finally {
      setBusy(false);
    }
  };

  const saveOps = async () => {
    setBusy(true);
    try {
      await updatePurchaseOrder(poId, {
        shipment_date: shipmentDate || null,
        ops_notes: opsNotes || null,
      });
      toast.success("PO updated");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const sendMsg = async () => {
    if (!msg.trim()) return;
    try {
      await sendPoMessage(poId, msg.trim());
      toast.success("Message sent");
      setMsg("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-[hsl(217,90%,25%)]" />
      </div>
    );
  }
  if (!po) {
    return (
      <div className="min-h-screen grid place-items-center text-neutral-500">
        Purchase Order not found.
      </div>
    );
  }

  const offer = po.selected_offer || {};
  const product = po.products?.[0] || {};

  return (
    <div className="min-h-screen bg-[hsl(60,5%,98%)]" data-testid={WORKSPACE.poDetail}>
      <header className="border-b border-neutral-200 bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/workspace/purchase-orders" className="inline-flex items-center gap-3">
            <span className="w-8 h-8 rounded-sm bg-[hsl(217,90%,25%)] grid place-items-center">
              <span className="text-white font-bold text-sm">D</span>
            </span>
            <span className="font-medium tracking-tight text-lg">DeMaxtore</span>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-400 ml-4 hidden sm:inline">
              Purchase Order
            </span>
          </Link>
          <div className="flex items-center gap-2">
            {(po.inspection_id || po.ready_for_inspection) && (
              <Button
                variant="outline"
                className="rounded-sm h-10"
                onClick={() =>
                  navigate(
                    po.inspection_id
                      ? `/workspace/inspections/${po.inspection_id}`
                      : `/workspace/inspections?po_id=${po.id}`
                  )
                }
              >
                View Inspection
              </Button>
            )}
            {(po.freightiq_id || po.ready_for_shipment) && (
              <Button
                variant="outline"
                className="rounded-sm h-10"
                onClick={() =>
                  navigate(
                    po.freightiq_id
                      ? `/workspace/freightiq/${po.freightiq_id}`
                      : `/workspace/freightiq?po_id=${po.id}`
                  )
                }
              >
                View FreightIQ
              </Button>
            )}
            {(po.tracking_id || po.ready_for_dispatch) && (
              <Button
                variant="outline"
                className="rounded-sm h-10"
                onClick={() =>
                  navigate(
                    po.tracking_id
                      ? `/workspace/shipments/${po.tracking_id}`
                      : `/workspace/shipments?po_id=${po.id}`
                  )
                }
              >
                Track Shipment
              </Button>
            )}
            <Button
              variant="outline"
              className="rounded-sm h-10"
              onClick={() => navigate(`/workspace/documents?po_id=${po.id}`)}
            >
              Documents
            </Button>
            {po.status === "Waiting Buyer Approval" && (
              <Button
                data-testid={WORKSPACE.poApproveBtn}
                disabled={busy}
                onClick={approve}
                className="rounded-sm h-10 bg-[hsl(217,90%,32%)] text-white"
              >
                <Check className="w-4 h-4 mr-2" /> Approve PO
              </Button>
            )}
            <Button
              variant="outline"
              className="rounded-sm h-10"
              onClick={() => window.print()}
            >
              Download PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 space-y-10">
        <button
          type="button"
          onClick={() => navigate("/workspace/purchase-orders")}
          className="inline-flex items-center text-sm text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> All purchase orders
        </button>

        {/* Summary */}
        <section className="border border-neutral-200 bg-white rounded-sm p-6 lg:p-8">
          <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[hsl(217,90%,35%)]">
            Purchase Order Summary
          </div>
          <h1 className="mt-3 text-3xl font-light tracking-tight font-mono text-[hsl(217,90%,25%)]">
            {po.number}
          </h1>
          <div className="mt-4 flex flex-wrap gap-2">
            <Badge className="rounded-sm bg-[hsl(217,90%,25%)] text-white uppercase text-[10px] tracking-wider">
              {po.status}
            </Badge>
            <Badge variant="outline" className="rounded-sm uppercase text-[10px] tracking-wider">
              Mfg: {po.manufacturer_status}
            </Badge>
            {po.ready_for_inspection && (
              <Badge className="rounded-sm bg-emerald-700 text-white uppercase text-[10px]">
                Ready for Inspection
              </Badge>
            )}
            {po.status === "Ready for Shipment" && (
              <Badge className="rounded-sm bg-teal-700 text-white uppercase text-[10px]">
                Ready for Shipment
              </Badge>
            )}
            {po.inspection_number && (
              <Badge variant="outline" className="rounded-sm uppercase text-[10px] tracking-wider font-mono">
                {po.inspection_number}
              </Badge>
            )}
            {po.freightiq_number && (
              <Badge variant="outline" className="rounded-sm uppercase text-[10px] tracking-wider font-mono">
                {po.freightiq_number}
              </Badge>
            )}
            {po.tracking_number && (
              <Badge variant="outline" className="rounded-sm uppercase text-[10px] tracking-wider font-mono">
                {po.tracking_number}
              </Badge>
            )}
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Meta label="RFQ" value={po.rfq_number} />
            <Meta label="Buyer" value={po.buyer?.company || po.buyer?.name || "—"} />
            <Meta label="Manufacturer" value={po.manufacturer?.name} />
            <Meta label="Country" value={po.destination_country || po.manufacturer?.country} />
            <Meta label="Created" value={po.created_at ? new Date(po.created_at).toLocaleString() : "—"} />
            <Meta label="Expected shipment" value={po.shipment_date || "—"} />
          </div>
        </section>

        {/* Selected offer */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Selected Offer
          </h2>
          <div className="border border-neutral-200 bg-white rounded-sm p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <Meta label="Unit Price" value={`${offer.unit_price ?? po.unit_price} ${po.currency}`} />
            <Meta label="Quantity" value={po.quantity} />
            <Meta label="Total Price" value={`${po.total_amount} ${po.currency}`} />
            <Meta label="Packaging" value={po.packaging || offer.packaging} />
            <Meta label="Incoterm" value={po.incoterm || offer.incoterm} />
            <Meta label="Lead Time" value={po.lead_time || offer.lead_time} />
            <Meta label="Payment Terms" value={po.payment_terms || offer.payment_terms} />
            <Meta label="Offer Validity" value={po.offer_validity || offer.offer_validity} />
          </div>
        </section>

        {/* Products */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Products
          </h2>
          <div className="border border-neutral-200 bg-white rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(60,5%,96%)] text-left">
                <tr>
                  {["Product", "Specification", "Packaging", "Quantity", "Unit", "Container", "Notes"].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-neutral-500"
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-neutral-100">
                  <td className="px-4 py-3">{product.product || "—"}</td>
                  <td className="px-4 py-3">{product.specification || "—"}</td>
                  <td className="px-4 py-3">{product.packaging || "—"}</td>
                  <td className="px-4 py-3">{product.quantity ?? "—"}</td>
                  <td className="px-4 py-3">{product.unit || "—"}</td>
                  <td className="px-4 py-3">{product.container || "—"}</td>
                  <td className="px-4 py-3">{product.notes || "—"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Timeline */}
        <section data-testid={WORKSPACE.poTimeline}>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Order Timeline
          </h2>
          <ol className="border border-neutral-200 bg-white rounded-sm divide-y divide-neutral-100">
            {(po.timeline || []).map((t) => (
              <li key={t.key} className="p-4 flex items-center gap-3 text-sm">
                <span
                  className={`w-2.5 h-2.5 rounded-full ${
                    t.status === "done" ? "bg-emerald-600" : "bg-neutral-300"
                  }`}
                />
                <span className="font-medium flex-1">{t.label}</span>
                <span className="text-neutral-400 text-xs">
                  {t.at ? new Date(t.at).toLocaleString() : "Pending"}
                </span>
              </li>
            ))}
          </ol>
        </section>

        {/* Manufacturer card */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Manufacturer
          </h2>
          <div className="border border-neutral-200 bg-white rounded-sm p-6 text-sm">
            <div className="font-medium text-lg">{po.manufacturer?.name}</div>
            <div className="text-neutral-500 mt-1">
              {po.manufacturer?.city}
              {po.manufacturer?.country ? `, ${po.manufacturer.country}` : ""}
            </div>
            <div className="mt-2 text-neutral-500">{po.manufacturer?.email}</div>
          </div>
        </section>

        {/* Message */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Send Message
          </h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="Message operations or manufacturer via workspace…"
              className="h-11 rounded-sm flex-1"
            />
            <Button variant="outline" className="rounded-sm h-11" onClick={sendMsg}>
              <MessageSquare className="w-4 h-4 mr-2" /> Send
            </Button>
          </div>
        </section>

        {/* Ops panel */}
        {isAdmin && (
          <section
            data-testid={WORKSPACE.poOpsPanel}
            className="border border-[hsl(217,90%,25%)]/20 bg-white rounded-sm p-6 space-y-4"
          >
            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[hsl(217,90%,25%)]">
              Operations
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Shipment date
                </span>
                <Input
                  type="date"
                  value={shipmentDate}
                  onChange={(e) => setShipmentDate(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Ops notes
                </span>
                <Textarea
                  value={opsNotes}
                  onChange={(e) => setOpsNotes(e.target.value)}
                  className="mt-2 min-h-[90px] rounded-sm"
                />
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button disabled={busy} onClick={saveOps} variant="outline" className="rounded-sm h-10">
                Save changes
              </Button>
              {po.status === "Waiting Manufacturer Confirmation" && (
                <Button disabled={busy} onClick={confirmMfg} className="rounded-sm h-10 bg-[hsl(217,90%,25%)] text-white">
                  Confirm manufacturer
                </Button>
              )}
              {po.status === "Confirmed" && (
                <Button
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await startPoProduction(poId);
                      await load();
                    } finally {
                      setBusy(false);
                    }
                  }}
                  className="rounded-sm h-10"
                >
                  Start production
                </Button>
              )}
              {["Confirmed", "Production"].includes(po.status) && (
                <Button
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await markPoReadyForInspection(poId);
                      toast.success("Ready for inspection — inspection record created");
                      await load();
                    } finally {
                      setBusy(false);
                    }
                  }}
                  className="rounded-sm h-10 bg-emerald-700 text-white"
                >
                  Ready for Inspection
                </Button>
              )}
              {po.inspection_id && (
                <Button
                  variant="outline"
                  className="rounded-sm h-10"
                  onClick={() => navigate(`/workspace/inspections/${po.inspection_id}`)}
                >
                  Open Inspection
                </Button>
              )}
              {po.freightiq_id && (
                <Button
                  variant="outline"
                  className="rounded-sm h-10"
                  onClick={() => navigate(`/workspace/freightiq/${po.freightiq_id}`)}
                >
                  Open FreightIQ
                </Button>
              )}
              {po.tracking_id && (
                <Button
                  variant="outline"
                  className="rounded-sm h-10"
                  onClick={() => navigate(`/workspace/shipments/${po.tracking_id}`)}
                >
                  Open Tracking
                </Button>
              )}
            </div>
          </section>
        )}

        {po.rfq_id && (
          <Link
            to={`/workspace/rfq/${po.rfq_id}/offers`}
            className="text-sm text-[hsl(217,90%,25%)] hover:underline"
          >
            ← Back to offer comparison
          </Link>
        )}
      </main>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-400 font-semibold">{label}</div>
      <div className="mt-1 text-neutral-800 break-words">{value ?? "—"}</div>
    </div>
  );
}
